import React, {
    useCallback, useEffect, useRef, useState
} from 'react';
import { createPortal } from 'react-dom';
import styles from './index.module.scss';
import {
    getHost, getLinkPreview, LinkPreview
} from './linkPreview';

interface MarkdownLinkProps {
  href: string;
  children?: React.ReactNode;
  className?: string;
}

const MarkdownLink = ({
    href, children, className 
}: MarkdownLinkProps) => {
    const [showPreview, setShowPreview] = useState(false);
    const [preview, setPreview] = useState<LinkPreview | null>(null);
    const [loading, setLoading] = useState(false);
    const [previewStyle, setPreviewStyle] = useState<React.CSSProperties>({});
    const linkRef = useRef<HTMLAnchorElement>(null);
    const previewRef = useRef<HTMLDivElement>(null);
    const showTimerRef = useRef<NodeJS.Timeout | null>(null);
    const hideTimerRef = useRef<NodeJS.Timeout | null>(null);

    // 获取链接预览信息
    const fetchLinkPreview = async (url: string) => {
        if (!url || !url.startsWith('http')) return;
    
        setLoading(true);
        try {
            const previewData = await getLinkPreview(url);
            setPreview(previewData);
        } catch (error) {
            console.error('Failed to fetch link preview:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (showPreview && !preview) {
            fetchLinkPreview(href);
        }
    }, [showPreview, href, preview]);

    // 清理定时器
    useEffect(() => {
        return () => {
            if (showTimerRef.current) {
                clearTimeout(showTimerRef.current);
            }
            if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current);
            }
            // 确保组件卸载时隐藏预览
            setShowPreview(false);
        };
    }, []);

    // 计算最佳位置
    const calculatePosition = useCallback(() => {
        if (!linkRef.current) return;

        const linkRect = linkRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        // 预览卡片的预估尺寸
        const previewWidth = 280; // min-width
        const previewHeight = 120; // 预估高度

        // 检查水平方向的空间
        const rightSpace = viewportWidth - linkRect.left;
        const leftSpace = linkRect.right;
        
        // 检查垂直方向的空间
        const bottomSpace = viewportHeight - linkRect.bottom;
        const topSpace = linkRect.top;

        // 决定显示位置
        let left: number;
        let top: number;

        // 水平位置计算
        if (rightSpace >= previewWidth) {
            // 右侧有足够空间，显示在右侧，贴近链接
            left = linkRect.left;
        } else if (leftSpace >= previewWidth) {
            // 左侧有足够空间，显示在左侧，贴近链接
            left = linkRect.right - previewWidth;
        } else {
            // 两侧空间都不够，居中显示
            left = linkRect.left + (linkRect.width / 2) - (previewWidth / 2);
        }

        // 垂直位置计算
        if (bottomSpace >= previewHeight) {
            // 下方有足够空间，显示在下方，贴近链接
            top = linkRect.bottom + 10;
        } else if (topSpace >= previewHeight) {
            // 上方有足够空间，显示在上方，贴近链接
            top = linkRect.top - previewHeight;
        } else {
            // 上下空间都不够，显示在中间
            top = linkRect.top + (linkRect.height / 2) - (previewHeight / 2);
        }

        // 确保预览卡片不会超出视口边界
        const maxLeft = viewportWidth - previewWidth - 10;
        const maxTop = viewportHeight - previewHeight - 10;
        
        left = Math.max(10, Math.min(left, maxLeft));
        top = Math.max(10, Math.min(top, maxTop));

        setPreviewStyle({
            position: 'fixed',
            left: `${left}px`,
            top: `${top}px`,
            zIndex: 9999,
        });
    }, []);

    // 监听窗口大小变化和滚动
    useEffect(() => {
        if (showPreview) {
            // 延迟计算，确保预览内容已渲染
            const timer = setTimeout(calculatePosition, 0);
            
            window.addEventListener('resize', calculatePosition);
            window.addEventListener('scroll', calculatePosition);
            
            return () => {
                clearTimeout(timer);
                window.removeEventListener('resize', calculatePosition);
                window.removeEventListener('scroll', calculatePosition);
            };
        }
    }, [showPreview, calculatePosition]);

    const handleMouseEnter = () => {
        // 清除隐藏定时器
        if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }
        
        // 延迟显示预览
        showTimerRef.current = setTimeout(() => {
            setShowPreview(true);
        }, 300); // 300ms 延迟
    };

    const handleMouseLeave = () => {
        // 清除显示定时器
        if (showTimerRef.current) {
            clearTimeout(showTimerRef.current);
            showTimerRef.current = null;
        }
        
        // 延迟隐藏预览
        hideTimerRef.current = setTimeout(() => {
            setShowPreview(false);
        }, 100); // 100ms 延迟
    };

    const host = getHost(href);

    // 渲染预览卡片到 body
    const renderPreview = () => {
        if (!showPreview) return null;

        return createPortal(
            <div 
                ref={previewRef}
                className={styles.previewCard}
                style={previewStyle}
            >
                {loading ? (
                    <div className={styles.loading}>加载中...</div>
                ) : preview ? (
                    <>
                        <div className={styles.previewHeader}>
                            {preview.favicon ? (
                                <img 
                                    src={preview.favicon} 
                                    alt="favicon" 
                                    className={styles.favicon}
                                    onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                    }}
                                />
                            ) : null}
                            <span className={styles.host}>{preview.host}</span>
                        </div>
                        {preview.title ? <div className={styles.title}>{preview.title}</div> : null}
                        {preview.description ? <div className={styles.description}>{preview.description}</div> : null}
                    </>
                ) : null}
            </div>,
            document.body
        );
    };

    return (
        <div className={styles.linkContainer}>
            <a
                ref={linkRef}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={`${styles.link} ${className || ''}`}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {children || host}
            </a>
            {renderPreview()}
        </div>
    );
};

export default MarkdownLink; 